// /Users/kokougbeve/Documents/Scripts_Apps/vidmastro/frontend/src/components/ThumbnailWorkspace.tsx

'use client';

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Moveable from 'react-moveable';
import { nanoid } from 'nanoid';
import { useTaleImages } from '@/hooks/useTaleImages';
import { useFinishedThumbnails } from '@/hooks/useFinishedThumbnails';
import { useThumbUpdates } from '@/hooks/useThumbUpdates';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { OverlayToolbar } from '@/components/OverlayToolbar';
import { generateThumbnails, rasterizeAllThumbnails } from '@/lib/api-thumbnails';

type VariantVM = {
  id: string | null;
  title: string;
  subtitle: string;
  pngUrl: string | null;
  svgUrl: string | null;
  bgImageId?: string | null; // track picked bg
  baked?: boolean; // track if text overlays have been baked
};

type OverlayVM = {
  id: string;
  text: string;
  x: number;             // in 1280px coords
  y: number;             // in 1280px coords (baseline)
  rotate: number;        // deg
  scale: number;         // 1.0
  fontSize: number;      // px
  color: string;
  opacity: number;       // 0..1
  fontWeight: number;    // 400..900
  fontFamily: string;    // font family name
  align: 'left'|'center'|'right';
  stroke: string;
  strokeWidth: number;
  lineHeight: number;    // px
  shadow: boolean;
  background: string | null;
  padding: number;
  borderRadius: number;
};

type Props = {
  taleId: string;
  title: string;
  topic: string;
  audience: string;
  maxVariants?: number;
};

const BASE_W = 1280;
const BASE_H = 720;

// Available fonts for thumbnails - only embedded fonts to ensure server rendering matches
const AVAILABLE_FONTS = [
  { name: 'Inter', label: 'Inter (Clean)', category: 'Sans-serif' },
  { name: 'BrandSans', label: 'BrandSans (Bold)', category: 'Sans-serif' },
  { name: 'Poppins', label: 'Poppins (Bold)', category: 'Sans-serif' },
  { name: 'Bebas Neue', label: 'Bebas (Impact)', category: 'Display' },
  { name: 'Anton', label: 'Anton (Strong)', category: 'Display' },
  { name: 'Playfair Display', label: 'Playfair (Classic)', category: 'Serif' },
];

// Cultural context options for AI image generation
const CULTURAL_CONTEXTS = [
  { value: 'universal', label: '🌍 Universal', description: 'Diverse representation, no specific cultural markers' },
  { value: 'west-african', label: '🌍 West African', description: 'Traditional African setting with rich earth tones, baobab trees, traditional textiles' },
  { value: 'middle-eastern', label: '🏜️ Middle Eastern', description: 'Biblical desert setting with ancient architecture, olive trees, traditional robes' },
  { value: 'asian', label: '🏯 Asian', description: 'Traditional Asian elements with pagodas, cherry blossoms, harmonious natural settings' },
  { value: 'european', label: '🏰 European', description: 'Medieval/Renaissance architecture, stone churches, rolling green hills' },
  { value: 'latin-american', label: '🌺 Latin American', description: 'Vibrant colors, traditional textiles, colonial architecture, tropical vegetation' },
];

export default function ThumbnailWorkspace({ taleId, title, topic, audience, maxVariants = 3 }: Props) {
  const { items: lib, loading: libLoading } = useTaleImages(taleId);
  const { items: finishedThumbnails, saveFinishedThumbnail, getAvailableSlots } = useFinishedThumbnails(taleId);
  const { thumbs, connected, refresh } = useThumbUpdates(taleId);
  
  // Thumbnail generation state
  const [generating, setGenerating] = useState(false);
  const [rasterizing, setRasterizing] = useState(false);
  
  // Slot selection for saving finished thumbnails
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [showSlotSelector, setShowSlotSelector] = useState(false);
  
  // Cultural context for AI prompts
  const [culturalContext, setCulturalContext] = useState<string>('universal');
  
  // SEO title options
  const [seoOptions, setSeoOptions] = useState<string[]>([]);
  const [loadingSeoOptions, setLoadingSeoOptions] = useState(false);

  const [variants, setVariants] = useState<VariantVM[]>([
    { id: null, title, subtitle: `${topic} · ${audience}`, pngUrl: null, svgUrl: null, baked: false }
  ]);
  const [active, setActive] = useState(0);
  const v = variants[active];
  
  // Debug: Monitor variant state changes
  useEffect(() => {
    console.log('Variant state changed:', v);
  }, [v]);

  // editor state - start with ONE overlay
  const [overlays, setOverlays] = useState<OverlayVM[]>(() => [
    {
      id: nanoid(),
      text: title, // Default to tale title, will be replaced with SEO option
      x: BASE_W/2, y: BASE_H/2,
      rotate: 0, scale: 1,
      fontSize: 56, color: '#ffffff', opacity: 1,
      fontWeight: 700, fontFamily: 'Inter', align: 'center',
      stroke: '#000000', strokeWidth: 2,
      lineHeight: 64, shadow: true,
      background: null, padding: 12, borderRadius: 16,
    }
  ]);
  const [selectedId, setSelectedId] = useState<string | null>(overlays[0].id);
  const [editingText, setEditingText] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number>(0);
  const [toast, setToast] = useState<string|null>(null);
  const selected = useMemo(() => overlays.find(o => o.id === selectedId) || null, [overlays, selectedId]);

  const frameRef = useRef<HTMLDivElement>(null);
  const [frameSize, setFrameSize] = useState<{w:number; h:number}>({ w: 1280, h: 720 });
  
  // Use stable resize observer for frame sizing
  useResizeObserver(frameRef.current, ({ width, height }) => {
    setFrameSize({ w: width, h: height });
  });

  // Uniform scale system
  const scale = Math.min(frameSize.w / BASE_W, frameSize.h / BASE_H);
  const toCss = (n: number) => n * scale;

  // Constraint helper to keep overlays within bounds
  const constrain = useCallback((ov: OverlayVM, w = BASE_W, h = BASE_H) => {
    const textWidth = ov.text.length * ov.fontSize * 0.6;
    const textHeight = ov.fontSize * (ov.text.split('\n').length || 1);
    const pad = ov.padding || 0;
    const x = Math.max(textWidth/2 + pad, Math.min(w - textWidth/2 - pad, ov.x));
    const y = Math.max(textHeight/2 + pad, Math.min(h - textHeight/2 - pad, ov.y));
    return { x, y };
  }, []);

  // Nudge selected overlay with constraints
  const nudgeSelected = useCallback((dx: number, dy: number) => {
    if (!selected) return;
    setOverlays(prev => prev.map(ov => 
      ov.id === selected.id 
        ? { ...ov, ...constrain({ ...ov, x: ov.x + dx, y: ov.y + dy }) }
        : ov
    ));
  }, [selected, constrain]);

  // keep N variants
  useEffect(() => {
    setVariants(prev => {
      if (prev.length >= maxVariants) return prev;
      const extra = Array.from({ length: maxVariants - prev.length }, () => ({
        id: null, title, subtitle: `${topic} · ${audience}`, pngUrl: null, svgUrl: null, baked: false
      }));
      return [...prev, ...extra];
    });
  }, [maxVariants, title, topic, audience]);


  /** Helpers */
  const isBakedUrl = (u?: string | null) => !!u && u.includes('/download.png');

  const setV = (patch: Partial<VariantVM>) => {
    console.log('setV called with patch:', patch, 'for active variant:', active);
    setVariants(prev => {
      const newVariants = prev.map((it, i) => {
        if (i !== active) return it;
        
        // Never allow baked -> unbaked
        if (it.baked && patch.baked === false) {
          console.warn('Blocked attempt to regress from baked state (baked=false)');
          return { ...it, ...patch, baked: true, pngUrl: it.pngUrl, svgUrl: it.svgUrl };
        }
        
        // Merge patch
        let next = { ...it, ...patch };
        
        // If we have a baked URL already, do NOT accept a non-baked URL, even if patch.baked===true
        if (it.baked && isBakedUrl(it.pngUrl) && next.pngUrl && !isBakedUrl(next.pngUrl)) {
          console.warn('Blocked attempt to replace baked URL with non-baked URL');
          next.pngUrl = it.pngUrl;
        }
        
        // Once baked, always baked (sticky)
        if (it.baked || next.baked) {
          next.baked = true;
        }
        
        return next;
      });
      console.log('New variants state:', newVariants);
      return newVariants;
    });
  };

  const setOverlay = (id: string, patch: Partial<OverlayVM>) =>
    setOverlays(prev => prev.map(ov => ov.id === id ? { ...ov, ...patch } : ov));

  const toCanvasX = (cssPxX: number) => Math.round(cssPxX / scale);
  const toCanvasY = (cssPxY: number) => Math.round(cssPxY / scale);

  async function ensureThumbId(): Promise<string> {
    if (v.id) return v.id;
    const res = await fetch('/api/thumbnails/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        taleId,
        idx: active + 1,
        title: v.title,
        subtitle: v.subtitle,
        style: 'classic',
        palette: { bg: '#0b0b0c', fg: '#ffffff', accent: '#818cf8' }
      })
    });
    const j = await res.json();
    setV({ id: j.id });
    return j.id as string;
  }

  async function applyBackground(imageId: string) {
    const id = await ensureThumbId();
    const res = await fetch(`/api/thumbnails/${id}/set-bg-image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ imageId, title: v.title, subtitle: v.subtitle })
    });
    if (!res.ok) return;
    const j = await res.json();
    const c = Date.now();
    setV({ 
      pngUrl: j.pngUrl ? `${j.pngUrl}&c=${c}` : j.pngUrl, 
      svgUrl: j.svgUrl ? `${j.svgUrl}&c=${c}` : j.svgUrl, 
      bgImageId: imageId, 
      baked: false 
    });
  }

  // Load SEO title options on mount
  useEffect(() => {
    loadSeoOptions();
  }, []);

  async function loadSeoOptions() {
    setLoadingSeoOptions(true);
    try {
      const res = await fetch('/api/ai/generate-seo-description', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          taleTitle: title,
          taleTopic: topic,
          taleAudience: audience,
          variantNumber: 1,
          overlays: []
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        const options = data.options || [title];
        setSeoOptions(options);
        
        // Update first overlay with the top SEO option
        if (options.length > 0 && overlays.length > 0) {
          setOverlays(prev => [
            { ...prev[0], text: options[0] },
            ...prev.slice(1)
          ]);
        }
      }
    } catch (err) {
      console.error('Failed to load SEO options:', err);
      setSeoOptions([title]);
    } finally {
      setLoadingSeoOptions(false);
    }
  }

  // Apply selected SEO option to the first overlay
  function applySeoOption(text: string) {
    if (overlays.length > 0) {
      setOverlays(prev => [
        { ...prev[0], text },
        ...prev.slice(1)
      ]);
    }
  }

  // Generate SEO description for saving (backward compatibility)
  async function generateSEODescription(variantNumber: number): Promise<string> {
    // Return the current first overlay text as SEO description
    return overlays[0]?.text || `${v.title} - Variant ${variantNumber}`;
  }

  // Generate new background with cultural context
  async function generateNewBackground() {
    try {
      const res = await fetch('/api/thumbnails/generate-background', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          taleId,
          title: v.title,
          topic,
          audience,
          style: 'storybook',
          culturalContext
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('Background generation queued:', data);
        // Show success message
        alert(`✨ New ${CULTURAL_CONTEXTS.find(c => c.value === culturalContext)?.label} background is being generated! It will appear in the list once ready.`);
      } else {
        const error = await res.text();
        console.error('Failed to generate background:', error);
        alert('Failed to generate background. Please try again.');
      }
    } catch (err) {
      console.error('Failed to generate background:', err);
      alert('Failed to generate background. Please try again.');
    }
  }

  // Generate thumbnails for the workspace
  async function generateThumbnailsForWorkspace() {
    setGenerating(true);
    try {
      await generateThumbnails(taleId, { count: 4, variants: ['A','B','C','D'], overwrite: false });
      refresh();
    } catch (error) {
      console.error('Thumbnail generation failed:', error);
      alert('Failed to generate thumbnails. Please try again.');
    } finally {
      setGenerating(false);
    }
  }

  // Rasterize all thumbnails to PNG
  async function rasterizeAllThumbnailsForWorkspace() {
    setRasterizing(true);
    try {
      await rasterizeAllThumbnails(taleId, { width: 1280, force: false });
      refresh();
    } catch (error) {
      console.error('Rasterization failed:', error);
      alert('Failed to rasterize thumbnails. Please try again.');
    } finally {
      setRasterizing(false);
    }
  }

  // Save (bake overlays on server)
  async function saveThumbnail() {
    const id = await ensureThumbId();
    
    // Generate SEO description for this variant
    const seoDescription = await generateSEODescription(active + 1);
    
    const payload = {
      title: v.title,
      subtitle: v.subtitle,
      seoDescription,
      overlays: overlays.map(o => ({
        text: o.text,
        x: Math.round(o.x),
        y: Math.round(o.y),
        rotate: o.rotate,
        scale: o.scale,
        fontSize: o.fontSize,
        color: o.color,
        opacity: o.opacity,
        fontWeight: o.fontWeight,
        fontFamily: o.fontFamily,
        align: o.align,
        stroke: o.stroke,
        strokeWidth: o.strokeWidth,
        lineHeight: o.lineHeight,
        shadow: o.shadow,
        background: o.background,
        padding: o.padding,
        borderRadius: o.borderRadius,
      }))
    };
    
    console.log('Saving thumbnail with overlays:', payload.overlays.length, 'overlays');
    
    const res = await fetch(`/api/thumbnails/${id}/apply-text-overlay`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    
    if (!res.ok) {
      console.error('Save failed:', await res.text());
      return;
    }
    
    const j = await res.json();
    console.log('Save response:', j);
    
    // Add client-side cache-buster for force refresh
    const ver = Date.now();
    const newVariant = { 
      id: variants[active]?.id || null,
      title: variants[active]?.title || '',
      subtitle: variants[active]?.subtitle || '',
      pngUrl: `${j.pngUrl}&c=${ver}`, 
      svgUrl: `${j.svgUrl}&c=${ver}`, 
      bgImageId: variants[active]?.bgImageId || null,
      baked: true 
    };
    
    console.log('Setting variant with baked=true:', newVariant);
    setV(newVariant);
    setLastSavedAt(Date.now());
    setToast('Saved with text overlays.');
  }

  function addTextOverlay() {
    const newOv: OverlayVM = {
      id: nanoid(),
      text: 'Click to edit',
      x: BASE_W/2, y: BASE_H/2,
      rotate: 0, scale: 1,
      fontSize: 48, color: '#ffffff', opacity: 1,
      fontWeight: 700, fontFamily: 'Inter', align: 'center',
      stroke: '#000000', strokeWidth: 2,
      lineHeight: 56, shadow: true,
      background: 'rgba(0,0,0,0.5)',
      padding: 12, borderRadius: 8,
    };
    setOverlays(prev => [...prev, newOv]);
    setSelectedId(newOv.id);
  }

  function removeSelected() {
    if (!selected) return;
    setOverlays(prev => prev.filter(o => o.id !== selected.id));
    setSelectedId(overlays.length ? overlays[0]?.id ?? null : null);
  }

  const duplicateSelected = useCallback(() => {
    if (!selected) return;
    const copy = { ...selected, id: nanoid(), x: selected.x + 20, y: selected.y + 20 };
    setOverlays(prev => [...prev, copy]);
    setSelectedId(copy.id);
  }, [selected]);



  // keyboard nudging with better bounds checking and text editing
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!selected) return;
      
      // Handle text editing
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setEditingText(selected.text);
        return;
      }
      
      const step = e.shiftKey ? 10 : 2;
      if (['ArrowUp','ArrowDown','ArrowLeft','ArrowRight','Delete','Backspace'].includes(e.key)) {
        e.preventDefault();
      }
      
      if (e.key === 'ArrowUp') nudgeSelected(0, -step);
      else if (e.key === 'ArrowDown') nudgeSelected(0, step);
      else if (e.key === 'ArrowLeft') nudgeSelected(-step, 0);
      else if (e.key === 'ArrowRight') nudgeSelected(step, 0);
      else if ((e.key === 'Delete' || e.key === 'Backspace') && document.activeElement === document.body) {
        removeSelected();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, overlays]);

  // Global keyboard shortcuts
  useEffect(() => {
    const onGlobalKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveThumbnail();
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        duplicateSelected();
      }
      if (e.key === 'Escape') {
        setSelectedId(null);
        setEditingText(null);
      }
    };
    window.addEventListener('keydown', onGlobalKey);
    return () => window.removeEventListener('keydown', onGlobalKey);
  }, [saveThumbnail, duplicateSelected]);

  // Save prompt on page close
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      // Only show prompt if there are unsaved changes (overlays exist)
      if (overlays.length > 0 && overlays.some(ov => ov.text.trim() !== '')) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    }
    
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [overlays]);

  /** Panel controls events */
  const onChangeSel = (patch: Partial<OverlayVM>) => selected && setOverlay(selected.id, patch);

  // Renderer
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px,1fr] gap-6">
      {/* LEFT: Controls */}
      <div className="space-y-4">
        {/* Title / Subtitle */}
        <div>
          <label className="text-sm font-medium mb-1 block" htmlFor="title-input">Title</label>
          <input
            id="title-input"
            className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            value={v.title}
            onChange={(e) => setV({ title: e.target.value })}
            placeholder="Enter title"
            title="Title"
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block" htmlFor="subtitle-input">Subtitle</label>
          <input
            id="subtitle-input"
            className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            value={v.subtitle}
            onChange={(e) => setV({ subtitle: e.target.value })}
            placeholder="Enter subtitle"
            title="Subtitle"
          />
        </div>

        {/* Cultural Context */}
        <div>
          <label className="text-sm font-medium mb-1 block" htmlFor="cultural-context-select">Cultural Context</label>
          <select
            id="cultural-context-select"
            className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            value={culturalContext}
            onChange={(e) => setCulturalContext(e.target.value)}
            title="Select cultural context for AI-generated backgrounds"
          >
            {CULTURAL_CONTEXTS.map(context => (
              <option key={context.value} value={context.value}>
                {context.label}
              </option>
            ))}
          </select>
          <div className="text-xs text-muted-foreground mt-1">
            {CULTURAL_CONTEXTS.find(c => c.value === culturalContext)?.description}
          </div>
        </div>

        {/* Thumbnail Generation */}
        <div>
          <div className="text-sm font-medium mb-2">Thumbnail Generation</div>
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={generateThumbnailsForWorkspace}
              disabled={generating || rasterizing}
              className="px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-sm"
              title="Generate 4 thumbnail SVGs (A/B/C/D variants)"
            >
              {generating ? 'Generating…' : 'Generate Thumbnails'}
            </button>

            <button
              onClick={rasterizeAllThumbnailsForWorkspace}
              disabled={generating || rasterizing}
              className="px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-sm"
              title="Render all SVGs to PNG"
            >
              {rasterizing ? 'Rasterizing…' : 'Rasterize All'}
            </button>
          </div>
          
          <div className="text-xs text-muted-foreground mb-2">
            Generate thumbnail templates first, then rasterize them to PNG format.
          </div>
          
          {thumbs.length > 0 && (
            <div className="text-xs text-muted-foreground">
              {thumbs.filter(t => t.status === 'generated').length} PNG • {thumbs.filter(t => t.status === 'queued' || t.status === 'generating').length} in progress
            </div>
          )}
        </div>

        {/* AI Background Images */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">AI Background Images ({lib.length})</div>
            <button
              onClick={generateNewBackground}
              className="px-2 py-1 text-xs rounded bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-50"
              disabled={!culturalContext}
              title="Generate new background with selected cultural context"
            >
              ✨ Generate
            </button>
          </div>
          <div className="text-xs text-muted-foreground mb-2">Select an AI-generated background image to use for your thumbnail</div>
          <div className="grid grid-cols-2 gap-2 max-h-[320px] overflow-auto rounded-lg border border-border p-2">
            {libLoading && <div className="col-span-2 text-xs text-muted-foreground p-2">Loading AI backgrounds…</div>}
            {!libLoading && lib.length === 0 && (
              <div className="col-span-2 text-xs text-muted-foreground p-2">No AI background images available. Generate some story images first.</div>
            )}
            {lib.map(img => (
              <button key={img.id}
                className="relative aspect-video rounded-md overflow-hidden bg-black hover:ring-2 hover:ring-blue-400 transition-all group"
                title={`Use "${img.title}" as background`}
                onClick={() => applyBackground(img.id)}
              >
                {img.url ? (
                  <>
                    <img src={img.url} className="w-full h-full object-cover" alt={img.title || 'AI Background'} />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
                    <div className="absolute bottom-1 left-1 right-1 text-[10px] text-white bg-black/50 rounded px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                      {img.title}
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full grid place-items-center text-[10px] text-muted-foreground">Loading…</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* SEO Title Options */}
        <div className="rounded-lg border border-border p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">✨ AI Title Suggestions</div>
            {loadingSeoOptions && <div className="text-xs text-muted-foreground">Generating...</div>}
          </div>
          {seoOptions.length > 0 ? (
            <div className="space-y-1.5">
              {seoOptions.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => applySeoOption(option)}
                  className={`w-full text-left text-xs px-2 py-1.5 rounded border transition-colors ${
                    overlays[0]?.text === option 
                      ? 'border-green-500 bg-green-500/10 text-green-700 dark:text-green-300' 
                      : 'border-border hover:bg-muted hover:border-blue-400'
                  }`}
                  title="Click to use this title"
                >
                  <span className="text-[10px] text-muted-foreground mr-1">#{idx + 1}</span>
                  {option}
                </button>
              ))}
              <button
                onClick={loadSeoOptions}
                className="w-full text-xs px-2 py-1 rounded border border-border hover:bg-muted text-muted-foreground"
              >
                🔄 Generate New Options
              </button>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Click to generate title ideas</div>
          )}
        </div>

        {/* Overlay list */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">Text Overlays</div>
            <div className="flex gap-2">
              <button onClick={addTextOverlay} className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-500">+ Add Text</button>
              <button onClick={removeSelected} disabled={!selected} className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-500 disabled:opacity-50">Remove</button>
            </div>
          </div>
          <div className="space-y-1 max-h-28 overflow-auto">
            {overlays.map(ov => (
              <button key={ov.id}
                onClick={() => setSelectedId(ov.id)}
                className={`w-full text-left text-xs px-2 py-1 rounded border ${selectedId === ov.id ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:bg-muted'}`}
              >
                {ov.text.slice(0, 42) || '(empty)'}
              </button>
            ))}
          </div>
        </div>

        {/* Selected overlay controls */}
        <div className="rounded-lg border border-border p-3 space-y-2">
          <div className="text-sm font-medium">Selected Text</div>
          {!selected && <div className="text-xs text-muted-foreground">Select a text overlay to edit.</div>}
          {selected && (
            <>
              <textarea
                className="w-full h-20 text-sm px-2 py-1 rounded border border-border bg-background"
                value={selected.text}
                onChange={e => onChangeSel({ text: e.target.value })}
                placeholder="Type your text (use line breaks for multi-line)"
              />
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs">
                  Font Size
                  <input type="number" className="w-full px-2 py-1 rounded border border-border bg-background"
                         value={selected.fontSize}
                         onChange={e => onChangeSel({ fontSize: Number(e.target.value) || 1 })} />
                </label>
                <label className="text-xs">
                  Weight
                  <input type="number" min={300} max={900} step={100}
                         className="w-full px-2 py-1 rounded border border-border bg-background"
                         value={selected.fontWeight}
                         onChange={e => onChangeSel({ fontWeight: Number(e.target.value) || 700 })} />
                </label>
                <label className="text-xs">
                  Font Family
                  <select className="w-full px-2 py-1 rounded border border-border bg-background"
                          value={selected.fontFamily}
                          onChange={e => onChangeSel({ fontFamily: e.target.value })}>
                    {AVAILABLE_FONTS.map(font => (
                      <option key={font.name} value={font.name}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs">
                  Color
                  <input type="color" className="w-full h-9 rounded border border-border"
                         value={selected.color}
                         onChange={e => onChangeSel({ color: e.target.value })} />
                </label>
                <label className="text-xs">
                  Opacity
                  <input type="range" min={0} max={1} step={0.05}
                         className="w-full"
                         value={selected.opacity}
                         onChange={e => onChangeSel({ opacity: Number(e.target.value) })} />
                </label>
                <label className="text-xs">
                  Stroke
                  <input type="color" className="w-full h-9 rounded border border-border"
                         value={selected.stroke}
                         onChange={e => onChangeSel({ stroke: e.target.value })} />
                </label>
                <label className="text-xs">
                  Stroke W
                  <input type="number" min={0} max={8} className="w-full px-2 py-1 rounded border border-border bg-background"
                         value={selected.strokeWidth}
                         onChange={e => onChangeSel({ strokeWidth: Number(e.target.value) || 0 })} />
                </label>
                <label className="text-xs">
                  Line Height
                  <input type="number" min={10} className="w-full px-2 py-1 rounded border border-border bg-background"
                         value={selected.lineHeight}
                         onChange={e => onChangeSel({ lineHeight: Number(e.target.value) || selected.fontSize * 1.12 })} />
                </label>
                <label className="text-xs">
                  Rotation°
                  <input type="number" className="w-full px-2 py-1 rounded border border-border bg-background"
                         value={selected.rotate}
                         onChange={e => onChangeSel({ rotate: Number(e.target.value) || 0 })} />
                </label>
                <label className="text-xs">
                  Scale
                  <input type="number" step={0.05} min={0.1} className="w-full px-2 py-1 rounded border border-border bg-background"
                         value={selected.scale}
                         onChange={e => onChangeSel({ scale: Number(e.target.value) || 1 })} />
                </label>
                <label className="text-xs">
                  Align
                  <select className="w-full px-2 py-1 rounded border border-border bg-background"
                          value={selected.align}
                          onChange={e => onChangeSel({ align: e.target.value as any })}>
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </label>
                <label className="text-xs">
                  Shadow
                  <select className="w-full px-2 py-1 rounded border border-border bg-background"
                          value={selected.shadow ? '1' : '0'}
                          onChange={e => onChangeSel({ shadow: e.target.value === '1' })}>
                    <option value="1">On</option>
                    <option value="0">Off</option>
                  </select>
                </label>
                <label className="text-xs">
                  BG Color
                  <input type="text" placeholder="e.g. rgba(0,0,0,0.35)"
                         className="w-full px-2 py-1 rounded border border-border bg-background"
                         value={selected.background || ''}
                         onChange={e => onChangeSel({ background: e.target.value || null })} />
                </label>
                <label className="text-xs">
                  BG Padding
                  <input type="number" className="w-full px-2 py-1 rounded border border-border bg-background"
                         value={selected.padding}
                         onChange={e => onChangeSel({ padding: Number(e.target.value) || 0 })} />
                </label>
                <label className="text-xs">
                  BG Radius
                  <input type="number" className="w-full px-2 py-1 rounded border border-border bg-background"
                         value={selected.borderRadius}
                         onChange={e => onChangeSel({ borderRadius: Number(e.target.value) || 0 })} />
                </label>
              </div>
            </>
          )}
        </div>

        {/* Variant tabs */}
        <div className="flex gap-2">
          {Array.from({ length: maxVariants }).map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                i === active
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80 text-muted-foreground'
              }`}
            >
              Variant {i + 1}
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT: Editor */}
      <div className="rounded-2xl bg-muted p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Editor</h3>
          <div className="text-sm text-muted-foreground">
            Variant {active + 1} • drag, rotate, and style text freely
          </div>
        </div>

        <div ref={frameRef} className="relative aspect-video rounded-xl overflow-hidden bg-black border border-border mb-4">
          {/* Background */}
          {v.pngUrl || v.svgUrl ? (
            <>
              <img src={v.pngUrl || v.svgUrl || ''} className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none" alt="Thumbnail background" />
              {v.baked && (
                <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                  ✓ Saved with Text
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 grid place-items-center text-muted-foreground">
              <div className="text-center">
                <div className="text-2xl mb-2">🎨</div>
                <div>Pick a background from the left to start editing</div>
              </div>
            </div>
          )}

          {/* Overlays */}
          {overlays.map(ov => {
            const isSel = ov.id === selectedId;
            
            const textStyle: React.CSSProperties = {
              color: ov.color,
              opacity: ov.opacity,
              fontWeight: ov.fontWeight,
              fontFamily: ov.fontFamily,
              fontSize: `${ov.fontSize * scale}px`,
              lineHeight: `${(ov.lineHeight || Math.round(ov.fontSize * 1.12)) * scale}px`,
              textAlign: ov.align as any,
              textShadow: ov.shadow ? '0 3px 6px rgba(0,0,0,0.6)' : undefined,
              padding: ov.padding ? `${ov.padding * scale}px` : undefined,
              borderRadius: ov.borderRadius ? `${ov.borderRadius * scale}px` : undefined,
              background: ov.background || undefined,
              whiteSpace: 'pre-line',
              WebkitTextStroke: ov.strokeWidth > 0 ? `${ov.strokeWidth}px ${ov.stroke}` : undefined,
              userSelect: 'none',
              minWidth: '20px',
              minHeight: '20px',
              cursor: isSel ? 'move' : 'pointer',
            };

            return (
              <div 
                key={ov.id}
                data-ov={ov.id}
                onMouseDown={() => setSelectedId(ov.id)}
                className={`absolute ${isSel ? 'ring-2 ring-blue-400 ring-offset-2' : 'hover:ring-1 hover:ring-blue-300'}`}
                // eslint-disable-next-line react/forbid-dom-props
                style={{ 
                  left: toCss(ov.x), 
                  top: toCss(ov.y), 
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'auto',
                  overflow: 'visible',
                  zIndex: isSel ? 10 : 1,
                }}
              >
                {/* eslint-disable-next-line react/forbid-dom-props */}
                <div style={{ 
                  transform: `rotate(${ov.rotate}deg) scale(${ov.scale})`, 
                  transformOrigin: 'center',
                  overflow: 'visible'
                }}>
                  {editingText !== null && ov.id === selectedId ? (
                    <input
                      type="text"
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      onBlur={() => {
                        if (editingText !== null) {
                          setOverlay(ov.id, { text: editingText });
                          setEditingText(null);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setOverlay(ov.id, { text: editingText });
                          setEditingText(null);
                        } else if (e.key === 'Escape') {
                          setEditingText(null);
                        }
                      }}
                      autoFocus
                      aria-label="Edit text overlay"
                      placeholder="Enter text..."
                      title="Edit text overlay - Press Enter to save, Escape to cancel"
                      style={{
                        ...textStyle,
                        background: 'rgba(255,255,255,0.9)',
                        color: '#000',
                        border: '2px solid #3b82f6',
                        outline: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        minWidth: '100px',
                      }}
                    />
                  ) : (
                    /* eslint-disable-next-line react/forbid-dom-props */
                    <div 
                      style={textStyle}
                      onDoubleClick={() => setEditingText(ov.text)}
                      title="Double-click to edit text"
                    >
                      {ov.text}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Moveable for selected overlay */}
          {selected && (
            <Moveable
              target={frameRef.current?.querySelector(`[data-ov="${selected.id}"]`) as HTMLElement}
              snappable
              snapCenter
              draggable
              rotatable
              throttleDrag={0}
              throttleRotate={0}
              origin={false}
              // Visual guidelines
              horizontalGuidelines={[toCss(BASE_H/2)]}
              verticalGuidelines={[toCss(BASE_W/2)]}
              bounds={{ left: 0, top: 0, right: frameSize.w, bottom: frameSize.h }}

              onDragStart={e => {
                e.set([toCss(selected.x), toCss(selected.y)]);
              }}
              onDrag={e => {
                const [dx, dy] = e.beforeTranslate;
                setOverlay(selected.id, { x: toCanvasX(dx), y: toCanvasY(dy) });
              }}
              onRotateStart={e => {
                e.set(selected.rotate);
              }}
              onRotate={e => {
                setOverlay(selected.id, { rotate: e.beforeRotate });
              }}
            />
          )}

          {/* Floating inline toolbar - temporarily disabled for debugging */}
          {/* <OverlayToolbar
            visible={!!selected}
            xCss={selected ? toCss(selected.x) : 0}
            yCss={selected ? toCss(selected.y) : 0}
            scale={scale}
            values={{
              fontFamily: selected?.fontFamily || 'Inter',
              fontSize: selected?.fontSize || 48,
              fontWeight: selected?.fontWeight || 700,
              align: selected?.align || 'center',
              color: selected?.color || '#ffffff',
              background: selected?.background ?? null,
              stroke: selected?.stroke || '#000000',
              strokeWidth: selected?.strokeWidth || 0,
              shadow: !!selected?.shadow,
            }}
            onChange={(patch) => selected && setOverlay(selected.id, patch)}
            onDuplicate={duplicateSelected}
            onDelete={removeSelected}
          /> */}

          {/* Visual guides - temporarily disabled for debugging */}
          {/* <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{ outline: `${1*scale}px dashed rgba(255,255,255,0.25)` }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              left: toCss(BASE_W*0.05),
              top: toCss(BASE_H*0.05),
              right: toCss(BASE_W*0.05),
              bottom: toCss(BASE_H*0.05),
              border: `${1*scale}px dashed rgba(255,255,255,0.25)`,
              borderRadius: toCss(8),
            }}
          />
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div
              style={{
                position: 'absolute',
                left: toCss(BASE_W/2),
                top: 0,
                bottom: 0,
                width: 1,
                background: 'rgba(255,255,255,0.15)',
                transform: 'translateX(-0.5px)'
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: toCss(BASE_H/2),
                left: 0,
                right: 0,
                height: 1,
                background: 'rgba(255,255,255,0.15)',
                transform: 'translateY(-0.5px)'
              }}
            />
          </div> */}


        </div>

        <div className="space-y-3">
          {/* Status Indicator */}
          <div className="text-xs text-center p-2 rounded-lg border">
            {!v.pngUrl && !v.svgUrl ? (
              <span className="text-orange-600">⚠️ No background selected</span>
            ) : v.baked ? (
              <span className="text-green-600">✅ Thumbnail saved with text overlays</span>
            ) : (
              <span className="text-blue-600">📝 Background ready - add text and save</span>
            )}
            <div className="text-xs text-gray-500 mt-1">
              Debug: pngUrl={v.pngUrl ? 'yes' : 'no'}, baked={v.baked ? 'true' : 'false'}, overlays={overlays.length}
            </div>
          </div>

          {/* Slot Selector for Finished Thumbnails */}
          {showSlotSelector && (
            <div className="p-4 border rounded-lg bg-muted/50">
              <div className="text-sm font-medium mb-3">Save to Finished Thumbnails</div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[1, 2, 3].map(slot => {
                  const isUsed = finishedThumbnails.some(t => t.slotNumber === slot);
                  const isSelected = selectedSlot === slot;
                  return (
                    <button
                      key={slot}
                      onClick={() => setSelectedSlot(slot)}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : isUsed
                            ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed'
                            : 'border-gray-300 bg-white hover:border-blue-300 hover:bg-blue-50'
                      }`}
                      disabled={isUsed}
                    >
                      Slot {slot}
                      {isUsed && <div className="text-xs mt-1">Used</div>}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    if (!selectedSlot || !v.baked) return;
                    
                    try {
                      // Convert PNG URL to base64 data
                      const pngResponse = await fetch(v.pngUrl!);
                      const pngBlob = await pngResponse.blob();
                      const pngData = await new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.readAsDataURL(pngBlob);
                      });
                      
                      // Convert SVG URL to data if available
                      let svgData: string | undefined;
                      if (v.svgUrl) {
                        const svgResponse = await fetch(v.svgUrl);
                        svgData = await svgResponse.text();
                      }
                      
                      await saveFinishedThumbnail(
                        selectedSlot,
                        v.title,
                        v.subtitle,
                        pngData.split(',')[1], // Remove data:image/png;base64, prefix
                        svgData,
                        overlays
                      );
                      
                      setShowSlotSelector(false);
                      setSelectedSlot(null);
                    } catch (error) {
                      console.error('Failed to save finished thumbnail:', error);
                      alert('Failed to save finished thumbnail');
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium disabled:opacity-50"
                  disabled={!selectedSlot || !v.baked}
                >
                  Save to Slot {selectedSlot}
                </button>
                <button
                  onClick={() => {
                    setShowSlotSelector(false);
                    setSelectedSlot(null);
                  }}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-400 text-white rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <button
            onClick={saveThumbnail}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={overlays.length === 0 && !v.pngUrl && !v.svgUrl}
          >
            💾 Save Thumbnail (Bake to PNG)
          </button>

          <button
            onClick={() => setShowSlotSelector(!showSlotSelector)}
            className="w-full px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!v.baked}
          >
            🎯 Save to Finished Thumbnails ({finishedThumbnails.length}/3)
          </button>

          <a
            href={v.baked && v.id ? `/api/thumbnails/${v.id}/download.png?c=${lastSavedAt}` : '#'}
            download={v.baked ? `${v.title || 'thumbnail'}-variant-${active + 1}.png` : undefined}
            className={`block text-center w-full px-4 py-2 rounded-lg font-medium ${
              v.baked ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-muted text-muted-foreground pointer-events-none'
            }`}
            title={v.baked ? 'Download thumbnail with text overlays' : 'Save thumbnail first to bake text overlays'}
          >
            {v.baked ? '⬇️ Download with Text' : '⬇️ Download Background Only'}
          </a>

          <div className="text-xs text-muted-foreground text-center">
            Tip: Hold <kbd>Shift</kbd> while using arrow keys to nudge faster.  
            Use the "BG Color" on the selected text to add a rounded label.
            <br />
            <strong>Save your thumbnail first to download with text overlays!</strong>
          </div>
        </div>
      </div>

      {/* Save confirmation toast */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-black/80 text-white text-sm px-3 py-2 rounded"
          onAnimationEnd={() => setToast(null)}
        >
          {toast}
        </div>
      )}
    </div>
  );
}